/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Acquire an Office SSO bootstrap token from the host application.
 *
 * This token is NOT an Ecsper token. It is a Microsoft Identity Platform
 * JWT scoped to the add-in's exposed API (for example `api://dev.addin.ecsper.io/{guid}`).
 * It must be exchanged via POST /platform/auth/office-sso for an Ecsper
 * context token.
 *
 * @returns The Office SSO bootstrap token string
 * @throws {@link OfficeSSOBootstrapError} when Office SSO cannot return a token (caller may fall back to dialog auth)
 *
 * Source: CTR-PL-MS-OFFICESSO-001 v1.0.0 §3.2 (`office_sso_token` field)
 */
export async function acquireOfficeSSOToken(): Promise<string> {
  try {
    return await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true });
  } catch (cause: unknown) {
    const code = extractOfficeAuthErrorCode(cause);
    const message = describeOfficeBootstrapFailure(code, cause);
    if (cause instanceof Error) {
      throw new OfficeSSOBootstrapError(code, message, { cause });
    }
    throw new OfficeSSOBootstrapError(code, message);
  }
}

/** Office SSO API error codes surfaced by `OfficeRuntime.auth.getAccessToken`. */
export const OFFICE_SSO_NOT_SUPPORTED = 13_001;
export const OFFICE_SSO_CONSENT_REQUIRED = 13_002;
export const OFFICE_SSO_USER_MISMATCH = 13_003;
export const OFFICE_SSO_INVALID_RESOURCE = 13_004;
export const OFFICE_SSO_AUTH_EXPIRED = 13_005;

export class OfficeSSOBootstrapError extends Error {
  public override readonly name = 'OfficeSSOBootstrapError';
  public readonly officeErrorCode: number | undefined;

  public constructor(
    officeErrorCode: number | undefined,
    message: string,
    options?: { cause?: Error },
  ) {
    super(message, options);
    this.officeErrorCode = officeErrorCode;
  }
}

function extractOfficeAuthErrorCode(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const candidate = error as { code?: unknown };
  if (typeof candidate.code === 'number' && Number.isFinite(candidate.code)) {
    return candidate.code;
  }
  if (typeof candidate.code === 'string') {
    const parsed = Number(candidate.code);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function describeOfficeBootstrapFailure(code: number | undefined, cause: unknown): string {
  switch (code) {
    case OFFICE_SSO_NOT_SUPPORTED:
      return 'Office single sign-on is not available for this session. Use dialog sign-in or restart the add-in.';
    case OFFICE_SSO_CONSENT_REQUIRED:
      return 'Administrator consent or user consent is required before Office single sign-on can continue.';
    case OFFICE_SSO_USER_MISMATCH:
      return 'The signed-in Office account does not match the account required for this add-in.';
    case OFFICE_SSO_INVALID_RESOURCE:
      return 'The Office SSO resource configuration does not match this add-in. Contact your administrator.';
    case OFFICE_SSO_AUTH_EXPIRED:
      return 'Office authentication has expired. Sign in again and retry.';
    default:
      return cause instanceof Error ? cause.message : 'Office single sign-on failed.';
  }
}
